import React, { useEffect, useState } from "react";
import ModalOrDrawer from "ui/ModalOrDrawer";
import { Button } from "ui/Button";
import { Input } from "ui/Input";
import type { ChatConversation } from "types/chat";
import { useUpdateChatConversationMutation } from "hooks/queries/useChatQueries";

interface ChatSettingsProps {
  conversation: ChatConversation;
  isOpen: boolean;
  onClose: () => void;
}

const ChatSettings: React.FC<ChatSettingsProps> = ({ conversation, isOpen, onClose }) => {
  const [title, setTitle] = useState<string>(conversation.title || "");
  const [pinned, setPinned] = useState<boolean>(Boolean(conversation.current_user_member_properties?.is_pinned));
  const [hidden, setHidden] = useState<boolean>(Boolean(conversation.current_user_member_properties?.is_hidden));
  const updateConversationMutation = useUpdateChatConversationMutation(conversation.public_id);

  useEffect(() => {
    setTitle(conversation.title || "");
    setPinned(Boolean(conversation.current_user_member_properties?.is_pinned));
    setHidden(Boolean(conversation.current_user_member_properties?.is_hidden));
  }, [conversation]);

  const handleSave = async (): Promise<void> => {
    await updateConversationMutation.mutateAsync({
      title: title.trim() || null,
      pinned,
      hidden,
    });
    onClose();
  };

  return (
    <ModalOrDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Chat settings"
      stickyFooter={true}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateConversationMutation.isPending}>
            {updateConversationMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conversation.kind !== "direct" ? (
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="chat-settings-title">Title</label>
            <Input
              id="chat-settings-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Conversation title"
            />
          </div>
        ) : null}

        <label className="flex items-center justify-between gap-4 rounded-xl border border-eoe-border-main dark:border-eoe-dark-border-main p-3">
          <span className="text-sm font-medium">Pin this chat</span>
          <input
            type="checkbox"
            checked={pinned}
            onChange={(event) => setPinned(event.target.checked)}
            className="toggle-switch-input"
          />
        </label>

        <label className="flex items-center justify-between gap-4 rounded-xl border border-eoe-border-main dark:border-eoe-dark-border-main p-3">
          <span className="text-sm font-medium">Hide this chat from your main inbox</span>
          <input
            type="checkbox"
            checked={hidden}
            onChange={(event) => setHidden(event.target.checked)}
            className="toggle-switch-input"
          />
        </label>
      </div>
    </ModalOrDrawer>
  );
};

export default ChatSettings;
